import React, { useState } from "react";
import { AlertTriangle, Check, XCircle } from "lucide-react";

function valueEntries(values) {
  return Object.entries(values || {}).filter(([, value]) => value || value === 0);
}

const yesBtn = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const noBtn = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

export default function AssistantConfirmCard({ action, busy, onConfirm, onCancel, onSave }) {
  const [editing, setEditing] = useState(false);
  const editable = action.editable || null;
  const canEdit = (key) => !editable || editable.indexOf(key) !== -1;
  const [draft, setDraft] = useState(() => {
    const next = {};
    valueEntries(action.values).forEach(([key, value]) => {
      if (canEdit(key)) next[key] = String(value);
    });
    return next;
  });
  const duplicate = action.result === "possible_duplicate";
  const match = action.possibleDuplicate || {};
  const disabled = Boolean(busy);
  return (
    <div className="flex gap-2" data-testid="agent-confirm-card" data-action-id={action.actionId}>
      <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 bg-amber-600/20 text-amber-400">
        <AlertTriangle size={14} />
      </div>
      <div className="flex flex-col max-w-[85%] items-start">
      <div className="rounded-2xl px-3.5 py-2.5 bg-amber-950/30 border border-amber-800/40 text-slate-300 rounded-tl-sm">
        <p className="text-[11px] font-medium text-amber-400 uppercase tracking-wider mb-1">Confirm Action</p>
        <p className="text-[13px] leading-relaxed">{action.description}</p>
        {action.filename ? <p className="text-[13px] mt-1">{`From ${action.filename}`}</p> : null}
        {action.duplicate ? <p className="text-[13px] mt-1 text-amber-200">{action.duplicate}</p> : null}
        {valueEntries(action.edits || action.values).filter(([key]) => !editing || !canEdit(key)).map(([key, value]) => (
          <p key={key} className="text-[13px]">{`${key}: ${value}`}</p>
        ))}
        {editing && Object.keys(draft).map((key) => (
          <label key={key} className="block text-[12px] mt-1">
            {key}
            <input
              data-testid={`edit-field-${action.actionId}-${key}`}
              value={draft[key]}
              onChange={(event) => setDraft((prev) => ({ ...prev, [key]: event.target.value }))}
              className="mt-0.5 w-full text-sm text-slate-800 rounded px-2 py-1"
            />
          </label>
        ))}
        {duplicate ? (
          <>
            <p className="text-[13px] leading-relaxed mt-2 text-amber-200">
              {`Possible duplicate: ${match.title || "Untitled"}, created ${match.created_at || ""}, ${match.status || ""}.`}
            </p>
            <div className="flex flex-wrap gap-2 mt-2.5">
              <button type="button" disabled={disabled} onClick={() => onConfirm(action.actionId, "add_anyway")} className={yesBtn}><Check size={12} />Add anyway</button>
              <button type="button" disabled={disabled} onClick={() => onConfirm(action.actionId, "use_existing")} className={noBtn}>Use existing</button>
              <button type="button" disabled={disabled} onClick={() => onCancel(action.actionId)} className={noBtn}><XCircle size={12} />Cancel</button>
            </div>
          </>
        ) : action.status === "pending" ? (
          <div className="flex flex-wrap gap-2 mt-2.5">
            {onSave && editing ? (
              <button type="button" onClick={() => { onSave(action.actionId, draft); setEditing(false); }} className={yesBtn}>Save</button>
            ) : null}
            {onSave && !editing ? (
              <button type="button" disabled={disabled} onClick={() => setEditing(true)} className={noBtn}>Edit</button>
            ) : null}
            <button type="button" disabled={disabled || editing} onClick={() => onConfirm(action.actionId)} className={yesBtn}><Check size={12} />Confirm</button>
            <button type="button" disabled={disabled} onClick={() => onCancel(action.actionId)} className={noBtn}><XCircle size={12} />Cancel</button>
          </div>
        ) : (
          <p className="text-xs mt-2">{action.status === "accepted" ? "Accepted" : "Skipped"}</p>
        )}
      </div>
      </div>
    </div>
  );
}
